import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import Pagination from "../../Components/Pagination";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";

const PAGE_SIZE = 25;

interface Brand {
  brand_id: number;
  shop_id: number;
  brand_name: string;
  brand_description: string;
  brand_image: string;
  brand_image_url?: string;
  created_at: string;
  updated_at: string;
}

interface EditBrand {
  brand_id?: number;
  shop_id?: number;
  brand_name: string;
  brand_description: string;
  brand_image: string;
  brand_image_url?: string;
}

interface Shop {
  shop_id: number;
  shop_name: string;
}

const BrandPage = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedBrands, setSelectedBrands] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editBrand, setEditBrand] = useState<EditBrand>({
    brand_name: "",
    brand_description: "",
    brand_image: ""
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchBrands = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/brand?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setBrands(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch brands. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/shop?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch shops. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  }, [cookies.auth]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleAdd = () => {
    setEditBrand({
      brand_name: "",
      brand_description: "",
      brand_image: ""
    });
    setImageFile(null);
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedBrands.length === 0) {
      alert("Please select at least one brand to update");
      return;
    }
    
    const brandToEdit = brands.find(brand => brand.brand_id === selectedBrands[0]);
    if (brandToEdit) {
      setEditBrand({
        brand_id: brandToEdit.brand_id,
        shop_id: brandToEdit.shop_id,
        brand_name: brandToEdit.brand_name,
        brand_description: brandToEdit.brand_description,
        brand_image: brandToEdit.brand_image || "",
        brand_image_url: brandToEdit.brand_image_url
      });
      setImageFile(null);
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedBrands.length === 0) {
      alert("Please select at least one brand to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const brandId of selectedBrands) {
        const response = await authenticatedFetch(`${API_BASE}/brand/${brandId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete brand ${brandId}. Status:`, response.status);
        }
      }
      setSelectedBrands([]);
      setShowDeleteConfirm(false);
      await fetchBrands();
    } catch (error) {
      console.error("Error deleting brands:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);
      let imageBase64: string | null = null;
      
      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile!);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          return;
        }
      }

      // Validate required fields
      if (!editBrand.brand_name.trim()) {
        snack.onResponse({ message: "Brand name is required", status: 400 });
        return;
      }
      
      if (!editBrand.shop_id || editBrand.shop_id === undefined) {
        snack.onResponse({ message: "Please select a shop", status: 400 });
        return;
      }

      const shopId = parseInt(editBrand.shop_id.toString());
      
      if (isNaN(shopId)) {
        snack.onResponse({ message: "Invalid shop ID", status: 400 });
        return;
      }

      const payload = {
        name: editBrand.brand_name.trim(),
        description: editBrand.brand_description.trim(),
        shop_id: shopId,
        image: imageBase64 || editBrand.brand_image
      };

      const url = isEdit 
        ? `${API_BASE}/brand/${editBrand.brand_id}`
        : `${API_BASE}/brand`;
      
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload)
      }, cookies.auth);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success response:", responseData);

        setShowEditModal(false);
        setShowAddModal(false);
        setEditBrand({
          brand_name: "",
          brand_description: "",
          brand_image: ""
        });
        setImageFile(null);
        setSelectedBrands([]);
        await fetchBrands();
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save brand"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving brand:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save brand", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBrands(brands.map(brand => brand.brand_id));
    } else {
      setSelectedBrands([]);
    }
  };

  const handleSelectBrand = (brandId: number, checked: boolean) => {
    if (checked) {
      setSelectedBrands([brandId]);
    } else {
      setSelectedBrands(selectedBrands.filter(id => id !== brandId));
    }
  };

  const filteredBrands = brands;

  const getShopName = (shopId: number | null | undefined) => {
    if (!shopId) return "Unknown";
    const shop = shops.find(s => s.shop_id === shopId);
    return shop ? shop.shop_name : "Unknown";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Brand</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by brand name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete Brand
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedBrands.length === brands.length && brands.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Brand ID</th>
              <th>Shop</th>
              <th>Brand Name</th>
              <th>Description</th>
              <th>Image</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredBrands.map((brand) => (
              <tr key={brand.brand_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand.brand_id)}
                      onChange={(e) => handleSelectBrand(brand.brand_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{brand.brand_id}</td>
                <td>{getShopName(brand.shop_id)}</td>
                <td>{brand.brand_name}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={brand.brand_description} maxLength={50} />
                </td>
                <td>
                  <img
                    src={brand.brand_image_url
                      ? resolveImageUrl(brand.brand_image_url)
                      : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }
                    alt={brand.brand_name}
                    className={style.brandImage}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }}
                  />
                </td>
                <td>{formatDate(brand.created_at)}</td>
                <td>{formatDate(brand.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add New Brand" : "Update Brand"}</h2>
            <div className={style.modalContent}>
              <input
                type="text"
                placeholder="Brand Name *"
                value={editBrand.brand_name}
                onChange={(e) => setEditBrand({...editBrand, brand_name: e.target.value})}
                className={style.modalInput}
                required
              />
              <select
                value={editBrand.shop_id || ""}
                onChange={(e) => {
                  const shopId = parseInt(e.target.value);
                  const shop = shops.find(s => s.shop_id === shopId);
                  if (shop) {
                    setEditBrand({...editBrand, shop_id: shopId});
                  }
                }}
                className={style.modalInput}
                required
              >
                <option value="">Select Shop *</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
              <textarea
                placeholder="Brand Description"
                value={editBrand.brand_description}
                onChange={(e) => setEditBrand({...editBrand, brand_description: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="file"
                onChange={handleImageChange}
                className={style.modalInput}
                accept="image/*"
              />
              {editBrand.brand_image && !imageFile && (
                <img
                  src={resolveImageUrl(editBrand.brand_image_url)}
                  alt="Current brand"
                  className={style.currentImage}
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMy43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                  }}
                />
              )}
              <div className={style.modalButtons}>
                <button
                  onClick={() => handleSave(showEditModal)}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : (showAddModal ? "Add Brand" : "Update Brand")}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className={style.cancelBtn}
                  disabled={isLoading}
                >
                Cancel
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete {selectedBrands.length} selected brand(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={style.cancelBtn}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandPage;